import { useCallback } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useApi } from '../api/context';
import { Combobox, type ComboOption } from '../design/primitives/Combobox';
import { resourceKeys } from '../api/hooks';
import type { FieldMeta, RelationMeta } from '../meta/types';

// Relation picker: an async Combobox against the target resource (`q=` search over its
// searchFields, first 20). Stores `valueField` (defaults to id). Resolves the label for a
// pre-set value by fetching the target row (or filtering by valueField when it isn't the id).

function rowLabel(row: Record<string, unknown>, displayField: string): string {
  const v = row[displayField];
  if (v != null && v !== '') return String(v);
  return String(row.name ?? row.ref ?? row.number ?? row.id ?? '');
}

/**
 * A relation's label from the query cache, without a request: any cached list page or item of
 * the target resource (a catalog the screen already loaded, the list the row came from). The
 * id-per-row GETs that used to follow every list and every inspector go away when the data is
 * already on the client; only a genuinely unknown id is fetched.
 */
export function cachedRowLabel(qc: QueryClient, rel: RelationMeta, value: string): string | null {
  const key = rel.valueField ?? 'id';
  for (const [, data] of qc.getQueriesData<unknown>({ queryKey: resourceKeys.all(rel.resource) })) {
    if (!data) continue;
    const pages = (data as { pages?: { data?: Record<string, unknown>[] }[] }).pages;
    const rows: Record<string, unknown>[] = pages ? pages.flatMap((p) => p.data ?? []) : typeof data === 'object' && (data as Record<string, unknown>)[key] !== undefined ? [data as Record<string, unknown>] : [];
    const hit = rows.find((r) => String(r[key]) === value);
    if (hit) return rowLabel(hit, rel.displayField);
  }
  return null;
}

export function useRelationSearch(rel: RelationMeta) {
  const api = useApi();
  return useCallback(
    async (q: string): Promise<ComboOption<string>[]> => {
      const filter = Object.entries(rel.filter ?? {}).map(([field, value]) => ({ field, op: 'eq' as const, value }));
      const page = await api.resource(rel.resource).list({ q: q || undefined, limit: 20, filter: filter.length ? filter : undefined, sort: rel.displayField });
      const key = rel.valueField ?? 'id';
      return page.data
        .filter((r) => r[key] != null)
        .map((r) => ({ value: String(r[key]), label: rowLabel(r, rel.displayField), sub: r.status ? String(r.status) : undefined }));
    },
    [api, rel.resource, rel.displayField, rel.valueField, rel.filter],
  );
}

export function useRelationLabel(rel: RelationMeta | undefined, value: string | null | undefined) {
  const api = useApi();
  const qc = useQueryClient();
  return useQuery({
    queryKey: ['relation-label', rel?.resource, rel?.valueField ?? 'id', value ?? ''],
    enabled: Boolean(rel && value),
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const cached = cachedRowLabel(qc, rel!, value!);
      if (cached) return cached;
      const key = rel!.valueField ?? 'id';
      if (key === 'id') {
        const row = await api.resource(rel!.resource).get(value!);
        return rowLabel(row, rel!.displayField);
      }
      const page = await api.resource(rel!.resource).list({ filter: [{ field: key, op: 'eq', value }], limit: 1 });
      const row = page.data[0];
      return row ? rowLabel(row, rel!.displayField) : null;
    },
  });
}

/** The label an expanded record carries for this field (`client_id` → `client_ref.label`), if any. */
export function refLabel(field: FieldMeta, row: Record<string, unknown> | undefined, value: unknown): string | null {
  if (!row || value == null) return null;
  const base = field.name.replace(/_id$/, '');
  const ref = (row[`${base}_ref`] ?? row[`${field.name}_ref`]) as { id?: unknown; label?: string | null } | null | undefined;
  if (ref && typeof ref === 'object' && ref.label && (ref.id === undefined || String(ref.id) === String(value))) return ref.label;
  const named = row[`${base}_name`];
  return typeof named === 'string' && named ? named : null;
}

export function RelationPicker({
  field,
  row,
  value,
  onChange,
  disabled,
  invalid,
  autoFocus,
  size,
  id,
  'aria-label': ariaLabel,
}: {
  field: FieldMeta;
  /** The record being edited: its expanded `<alias>_ref` labels answer the preset value without a request. */
  row?: Record<string, unknown>;
  value: string | null | undefined;
  onChange: (v: string | null, label?: string | null) => void;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  size?: 'sm' | 'md';
  id?: string;
  'aria-label'?: string;
}) {
  const rel = field.relation!;
  const api = useApi();
  const qc = useQueryClient();
  const load = useRelationSearch(rel);
  const resolve = useCallback(
    async (v: string) => {
      const key = rel.valueField ?? 'id';
      const carried = refLabel(field, row, v);
      if (carried) return carried;
      const cached = cachedRowLabel(qc, rel, v);
      if (cached) return cached;
      try {
        if (key === 'id') return rowLabel(await api.resource(rel.resource).get(v), rel.displayField);
        const page = await api.resource(rel.resource).list({ filter: [{ field: key, op: 'eq', value: v }], limit: 1 });
        return page.data[0] ? rowLabel(page.data[0], rel.displayField) : null;
      } catch {
        return null;
      }
    },
    [api, qc, rel, field, row],
  );
  return (
    <Combobox<string>
      id={id}
      value={value ?? null}
      onChange={(v, o) => onChange(v, o?.label ?? null)}
      loadOptions={load}
      resolveLabel={resolve}
      placeholder={`Search ${field.label.toLowerCase()}…`}
      disabled={disabled}
      invalid={invalid}
      autoFocus={autoFocus}
      size={size}
      aria-label={ariaLabel}
      emptyText={`No ${field.label.toLowerCase()} found`}
    />
  );
}

/** Read-only relation label: uses `<name>_ref.label` / `<base>_name` on the row when present, else resolves. */
export function RelationLabel({ field, row, value }: { field: FieldMeta; row?: Record<string, unknown>; value: unknown }) {
  // The API's `expand=` puts `<alias>_ref: { id, label }` on the row, alias = column minus `_id`
  // (`client_id` → `client_ref`). Legacy DTOs carry `<alias>_name` instead.
  const baseName = field.name.replace(/_id$/, '');
  const refValue = row?.[`${baseName}_ref`] ?? row?.[`${field.name}_ref`];
  const inline = refLabel(field, row, value) ?? (typeof refValue === 'string' ? refValue : undefined);
  const q = useRelationLabel(inline ? undefined : field.relation, typeof value === 'string' ? value : null);
  if (value == null || value === '') return <span className="hf-muted">—</span>;
  if (inline) return <span>{inline}</span>;
  if (q.data) return <span>{q.data}</span>;
  // Still resolving: a placeholder. Not resolvable (deleted, or not in the connected system's catalog):
  // say so in words, never an id fragment; the id stays in the tooltip.
  if (q.isLoading) return <span className="hf-muted">…</span>;
  return <span className="hf-muted" title={String(value)}>Unknown {field.label.toLowerCase()}</span>;
}

export { resourceKeys };
